import { type FC, useEffect, useState } from 'react';

import Badge from 'molecules/Badge';
import Button from 'molecules/Button';
import Heading from 'molecules/Heading';
import Image from 'molecules/Image';
import Link from 'molecules/Link';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import ResourceConversionPanel from 'organisms/Cards/ResourceConversionPanel';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';

import Filters from 'layout/Listing/variants/connectors/components/Filters';
import NoResults from 'layout/Listing/variants/connectors/components/NoResults';
import SearchBar from 'layout/Listing/variants/connectors/components/SearchBar';
import useFilteredConnectors from 'layout/Listing/variants/connectors/hooks/useFilteredConnectors';
import {
  setConnectorsResultCount,
  useConnectorsFilter,
  useConnectorsSearch,
  useResultMax,
} from 'layout/Listing/variants/connectors/store';

import { hasArrayValues } from 'utils/array/arrays';
import { toArray } from 'utils/array/toArray';
import { getBuilderStateData } from 'utils/builder';
import { useIsDesktop } from 'utils/hooks/useIsDesktop';

import comingSoonData from 'data/comingSoonConnectors.json';
import connectorData from 'data/connectors.json';
import popularsConnectorsData from 'data/popularConnectors.json';

import type { ListingConfigProps } from 'layout/Listing/config';

type ConnectorItem = Connector;

type PopularConfig = {
  key: string;
  display?: {
    label?: string;
    image?: { url?: string };
  };
};

const mergeConnectorLists = (primary: ConnectorItem[], fallback: ConnectorItem[]): ConnectorItem[] => {
  const seen = new Set<string>();
  const result: ConnectorItem[] = [];

  const addItem = (item: ConnectorItem) => {
    const key = item?.key || item?.display?.label;
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    result.push(item);
  };

  primary.forEach(addItem);
  fallback.forEach(addItem);

  return result;
};

const ConnectorsListing: FC<ListingConfigProps> = props => {
  const {
    sectionProps,
    rest: { conversionPanel, builderState },
  } = splitSectionProps(props);

  const [isMobile, setIsMobile] = useState(false);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsMobile(window.innerWidth < 1024);
    }
  }, []);

  const connectors = toArray<ConnectorItem>(connectorData);

  const rawLatestComingSoon = getBuilderStateData('listingData', builderState);
  const comingSoonLatest = toArray<ConnectorItem>(rawLatestComingSoon);
  const comingSoonLocal = toArray<ConnectorItem>(comingSoonData);

  const comingSoon = hasArrayValues(comingSoonLatest)
    ? mergeConnectorLists(comingSoonLatest, comingSoonLocal)
    : comingSoonLocal;

  const allConnectors = [...connectors, ...comingSoon];

  if (!hasArrayValues(allConnectors)) {
    console.warn('[ConnectorsListing] No items to render – check builderState/listingData and JSON sources');
  }

  const popularLocal = toArray<PopularConfig>(popularsConnectorsData);
  const popularSource: PopularConfig[] = hasArrayValues(popularLocal) ? popularLocal : [];

  const popularConnectors = popularSource
    .map((pop: PopularConfig) => {
      const key = pop?.key || pop?.display?.label;
      if (!key) {
        return undefined;
      }

      return allConnectors.find((conn: ConnectorItem) => conn.key === key || conn.display?.label === key);
    })
    .filter((conn): conn is ConnectorItem => Boolean(conn));

  const search = useConnectorsSearch();
  const filter = useConnectorsFilter();
  const normalizedSearch = search.toLowerCase().trim();
  const normalizedFilter = filter.toLowerCase();

  const filteredPopular = popularConnectors
    .filter((conn: ConnectorItem) => conn.display?.label.toLowerCase().includes(normalizedSearch))
    .filter((conn: ConnectorItem) => {
      if (filter === 'View All') {
        return true;
      }

      return conn.display?.marketingCategories?.some(category => category.toLowerCase() === normalizedFilter);
    });

  const visiblePopularConnectors = filteredPopular.slice(0, 10);

  const filteredConnectors = useFilteredConnectors(allConnectors);
  const resultMax = useResultMax();
  const showMore = filteredConnectors.length < (resultMax || 0);

  const marketingCategories = new Set(
    allConnectors
      .map(c => c?.display?.marketingCategories)
      .filter((arr): arr is string[] => Array.isArray(arr) && arr.length > 0)
      .flat(),
  );

  const renderConnectorCard = (connector: ConnectorItem, keyPrefix = '') => {
    if (!connector?.display?.label) {
      return null;
    }

    const image = connector?.display?.iconBase64
      ? `data:image/png;base64, ${connector.display.iconBase64}`
      : connector?.display?.image?.url;

    return (
      <Link
        key={`${keyPrefix}${connector.key || connector.display.label}`}
        href={connector.key ? `/connectors/${connector.key}/` : ''}
        className="relative flex flex-col items-center rounded-lg border-[1.5px] border-solid border-primary-200 bg-primary-50 p-3"
        enablePreview={false}
      >
        {connector.comingSoon && (
          <div className="absolute right-0 top-0 z-20">
            <Badge label="Coming Soon" color="green" />
          </div>
        )}
        <div className="flex h-36 w-full items-center justify-center p-1.5">
          <Image
            url={image}
            alt={`Logo for ${connector.display.label}`}
            className="max-h-[100px] w-auto object-contain"
            height="100"
            width="100"
            noFill
          />
        </div>

        <div className="mt-2 w-full text-left">
          <span className="block font-spaceGrotesk text-sm font-bold leading-none text-gray-900">
            {connector.display.label}
          </span>
        </div>
      </Link>
    );
  };

  const content = (
    <>
      <div className="hidden w-full grid-cols-12 gap-6 lg:grid">
        <aside className="col-span-3">
          <div className="sticky top-5 flex h-fit w-full flex-col gap-10">
            <Filters label="Categories" items={['View All', ...marketingCategories]} />
            {conversionPanel?.value?.data && (
              <div className="hidden lg:block">
                <ResourceConversionPanel {...conversionPanel.value.data} theme="green" />
              </div>
            )}
          </div>
        </aside>

        <div className="col-span-9 flex flex-col gap-8">
          {isDesktop && (
            <div className="mb-4 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-xl">
                <Heading
                  as="h1"
                  heading="Connectors"
                  body="Explore our built-in connectors and powerful custom connector SDK."
                  alignment="left"
                />
              </div>
              <div className="w-full max-w-xs lg:max-w-sm">
                <SearchBar />
              </div>
            </div>
          )}

          {hasArrayValues(visiblePopularConnectors) && (
            <div className="flex w-full flex-col gap-4">
              <h2 className="font-spaceGrotesk text-lg font-semibold text-gray-900">Popular connectors</h2>
              <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
                {visiblePopularConnectors.map(connector => renderConnectorCard(connector, 'popular-'))}
              </div>
            </div>
          )}

          {hasArrayValues(filteredConnectors) ? (
            <div className="flex w-full flex-col gap-4">
              <h2 className="font-spaceGrotesk text-lg font-semibold text-gray-900">All connectors</h2>
              <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
                {filteredConnectors.map(connector => renderConnectorCard(connector))}
              </div>
            </div>
          ) : (
            <NoResults />
          )}

          {showMore && (
            <div className="mt-4 flex justify-center">
              <Button
                label="Load More Connectors"
                onClick={() => setConnectorsResultCount(15)}
                key="Load More Connectors"
                hierarchy="primary"
                className="!bg-buttonLightGradient !text-gray-900"
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex w-full flex-col gap-8 lg:hidden">
        {!isDesktop && (
          <div className="flex flex-col gap-6">
            <div className="max-w-xl">
              <Heading
                as="h1"
                heading="Connectors"
                body="Explore our built-in connectors and powerful custom connector SDK."
                alignment="left"
              />
            </div>
            <div className="w-full max-w-xs">
              <SearchBar />
            </div>
          </div>
        )}

        {hasArrayValues(visiblePopularConnectors) && (
          <div className="flex w-full flex-col gap-4">
            <h2 className="font-spaceGrotesk text-lg font-semibold text-gray-900">Popular connectors</h2>
            <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2">
              {visiblePopularConnectors.map(connector => renderConnectorCard(connector, 'popular-'))}
            </div>
          </div>
        )}

        <div>
          <Filters label="Categories" items={['View All', ...marketingCategories]} />
        </div>

        {hasArrayValues(filteredConnectors) ? (
          <div className="flex w-full flex-col gap-4">
            <h2 className="font-spaceGrotesk text-lg font-semibold text-gray-900">All connectors</h2>
            <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2">
              {filteredConnectors.map(connector => renderConnectorCard(connector))}
            </div>
          </div>
        ) : (
          <NoResults />
        )}

        {showMore && (
          <div className="mt-4 flex justify-center">
            <Button
              label="Load More Connectors"
              onClick={() => setConnectorsResultCount(15)}
              key="Load More Connectors mobile"
              hierarchy="primary"
              className="!bg-buttonLightGradient !text-gray-900"
            />
          </div>
        )}

        {conversionPanel?.value?.data && (
          <div className="mt-6">
            <ResourceConversionPanel {...conversionPanel.value.data} theme="green" />
          </div>
        )}
      </div>
    </>
  );

  return (
    // A div, not <main>: this listing renders inside the layout's <main>, so a
    // <main> here nested a second one on /connectors/. The sr-only directory
    // below is what makes that worth caring about -- a screen reader landing on
    // an ambiguous main landmark is exactly who that nav is for.
    <div className="z-50 flex flex-col">
      <Section {...sectionProps}>
        {isMobile ? content : <ScrollFadeSection delay={0.1} containPaint={false}>{content}</ScrollFadeSection>}
      </Section>
      <nav aria-label="All connectors directory" className="sr-only">
        {allConnectors
          .filter(c => c.key)
          .map(connector => (
            <a key={`seo-${connector.key}`} href={`/connectors/${connector.key}/`}>
              {connector.display?.label}
            </a>
          ))}
      </nav>
    </div>
  );
};

export default ConnectorsListing;
